import { useEffect, useState } from 'react';
import { phase10Api } from '../api/phase10Client';

const PLATFORMS = ['x', 'facebook', 'youtube'];

export function PerceptionPage() {
  const [summary, setSummary] = useState<any[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [platform, setPlatform] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [ingestMsg, setIngestMsg] = useState<string | null>(null);

  const refresh = () => {
    Promise.all([
      phase10Api.perceptionSummary(),
      phase10Api.perceptionSources(),
      phase10Api.perceptionPosts(platform ? { platform } : undefined),
    ])
      .then(([s, src, p]) => { setSummary(s); setSources(src); setPosts(p); })
      .catch((e) => setError(String(e)));
  };

  useEffect(() => { refresh(); }, [platform]);

  const handleAggregate = () => {
    phase10Api.aggregatePerception().then(() => { setIngestMsg('PPI index recomputed.'); refresh(); });
  };

  if (error) return <div className="error">{error}</div>;

  return (
    <div>
      <h1 className="page-title">Public Perception Index</h1>
      <p className="page-subtitle">
        Offline-first sentiment pipeline for X, Facebook, and YouTube. PPI adjusts qualitative scenario scores
        (positive/negative comments and reactions). Platform APIs will plug in when the model is complete.
      </p>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3>Data Sources</h3>
        <table className="data-table">
          <thead><tr><th>Platform</th><th>Status</th><th>Notes</th></tr></thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.id}>
                <td>{s.display_name}</td>
                <td>{s.active ? 'API active' : 'Mock / offline'}</td>
                <td style={{ fontSize: '0.85rem' }}>{s.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className="btn-primary" style={{ marginTop: '0.75rem' }} onClick={handleAggregate}>
          Recompute PPI Index
        </button>
        {ingestMsg && <p style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>{ingestMsg}</p>}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3>PPI by Policy Lever</h3>
        <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
          Qualitative adjustment shifts implementation scores on the 0–4 scale (±0.75 max).
        </p>
        <table className="data-table">
          <thead>
            <tr>
              <th>Variable</th>
              <th>Sentiment</th>
              <th>Volume</th>
              <th>Positive %</th>
              <th>Negative %</th>
              <th>Q Adjustment</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((r) => (
              <tr key={r.variable_id}>
                <td>{r.variable_name || r.variable_id}</td>
                <td style={{ color: r.sentiment_avg > 0 ? '#16a34a' : r.sentiment_avg < 0 ? '#dc2626' : '#6b7280' }}>
                  {r.sentiment_avg?.toFixed(2)}
                </td>
                <td>{r.volume}</td>
                <td>{((r.positive_pct || 0) * 100).toFixed(0)}%</td>
                <td>{((r.negative_pct || 0) * 100).toFixed(0)}%</td>
                <td>{r.qualitative_adjustment >= 0 ? '+' : ''}{r.qualitative_adjustment?.toFixed(2)}</td>
              </tr>
            ))}
            {summary.length === 0 && (
              <tr><td colSpan={6}>No PPI data — run seed_phase10.py or ingest mock posts.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Recent Posts (mock / offline)</h3>
        <div style={{ marginBottom: '0.75rem' }}>
          <label>
            Platform:{' '}
            <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
              <option value="">All</option>
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
        </div>
        <table className="data-table">
          <thead><tr><th>Platform</th><th>Variable</th><th>Content</th><th>Sentiment</th><th>Reactions</th></tr></thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id}>
                <td>{p.platform}</td>
                <td>{p.variable_id}</td>
                <td style={{ maxWidth: 320, fontSize: '0.85rem' }}>{p.content}</td>
                <td style={{ color: p.sentiment > 0 ? '#16a34a' : p.sentiment < 0 ? '#dc2626' : '#6b7280' }}>
                  {p.sentiment?.toFixed(2)}
                </td>
                <td>{p.reaction_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
